/**
 * Config efectivo recortado para soporte (sin secretos).
 * OTHERS: resto de claves de config.json (secretos redactados).
 */

const TOP_LEVEL_ALLOW = new Set([
  'theme',
  'darkMode',
  'devMode',
  'supportModeToggleEnabled',
  'dbName',
  'isForTesting',
  'local',
  'labelPrintMode',
  'bucketName',
  'region',
  'releaseDate',
  'frontend_url',
  'backendUrl',
  'authMethods',
  'showTotpRegistrationQr',
  'voiceCommandNavigate',
  'voiceCommandActions',
  'screenRecorder',
  'availableLanguages',
]);

const NESTED_ALLOW = {
  awareness: ['enableAutoRefresh', 'refreshOnlyProcedureFromAction'],
  businessRules: [
    'enableLockSession',
    'minsLockSession',
    'enableLogoutSession',
    'minsLogoutSession',
  ],
  consoleControl: ['activateConsoleLog', 'activateConsoleWarn'],
  workstationMode: ['askForUserCredentials', 'useQueriesTokenForActions', 'voiceCommandActions'],
};

/** Ya visibles en PLATFORM INFO / TRAZiT TESTING / RUNTIME CONFIG. */
const COVERED_TOP_LEVEL = new Set([
  ...TOP_LEVEL_ALLOW,
  'uiTestingBranch',
  'bot_ai',
  'mqtt',
  'dbName',
  'backendUrl',
  'releaseDate',
  'frontend_url',
]);

const SECRET_EXACT = new Set([
  'dbConType',
  'dbConHost',
  'dbConPort',
  'dbConUsername',
  'dbConPassword',
  'mqttUsername',
  'mqttPassword',
]);

const SECRET_KEY_RE = /password|secret|access[_-]?key|api[_-]?key|credential|queryToken/i;

function isMqttEnabled(config) {
  return config?.mqtt === true || config?.mqtt === 'true' || config?.mqtt === 1;
}

function formatValue(value) {
  if (value === null || value === undefined) return '---';
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function isSecretKey(key) {
  return SECRET_EXACT.has(key) || SECRET_KEY_RE.test(String(key || ''));
}

function formatOthersValue(key, value) {
  if (isSecretKey(key)) return '(redacted)';
  if (value == null) return '---';
  if (typeof value !== 'object') return formatValue(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)';
    if (value.every((v) => v == null || typeof v !== 'object')) return value.join(', ');
    if (value.every((v) => v && typeof v === 'object' && v.value)) {
      return value.map((v) => v.value).join(', ');
    }
    if (value.every((v) => v && typeof v === 'object' && v.url)) {
      return `${value.length} urls`;
    }
    return `${value.length} items`;
  }

  const parts = [];
  for (const [k, v] of Object.entries(value)) {
    if (isSecretKey(k)) {
      parts.push(`${k}: (redacted)`);
      continue;
    }
    if (k === 'requestTypes' && Array.isArray(v)) {
      parts.push(`requestTypes: ${v.map((x) => x?.value).filter(Boolean).join(', ') || `${v.length} items`}`);
      continue;
    }
    if (v && typeof v === 'object') {
      parts.push(`${k}: ${Array.isArray(v) ? `${v.length} items` : 'object'}`);
    } else {
      parts.push(`${k}: ${formatValue(v)}`);
    }
  }
  return parts.length ? parts.join(' | ') : 'object';
}

function appendNestedLines(lines, prefix, obj, allowedKeys) {
  if (!obj || typeof obj !== 'object') return;
  for (const key of allowedKeys) {
    if (obj[key] === undefined) continue;
    lines.push(`${prefix}.${key}: ${formatValue(obj[key])}`);
  }
}

/** Resumen de bot_ai sin claves ni URLs internas. */
function summarizeBotAi(botAi) {
  if (!Array.isArray(botAi) || botAi.length === 0) return null;
  return botAi
    .map((b) => {
      const name = b?.name || b?.provider || 'bot';
      const provider = b?.provider ? ` (${b.provider})` : '';
      const model = b?.model ? `, model ${b.model}` : '';
      return `${name}${provider}${model}`;
    })
    .join('; ');
}

/**
 * Bloque de texto plano para pegar en soporte / asistente IA.
 * @param {object} config - Config efectivo en runtime (p. ej. this.config del dashboard)
 */
export function buildSupportConfigBlock(config = {}) {
  const lines = ['=== RUNTIME CONFIG (support) ==='];

  for (const key of TOP_LEVEL_ALLOW) {
    if (config[key] === undefined) continue;
    lines.push(`${key}: ${formatValue(config[key])}`);
  }

  if (isMqttEnabled(config)) {
    lines.push('mqtt: enabled');
    if (config.mqttBrokerUrl) lines.push(`mqttBrokerUrl: ${config.mqttBrokerUrl}`);
    if (config.mqttNotificationsTopic) {
      lines.push(`mqttNotificationsTopic: ${config.mqttNotificationsTopic}`);
    }
    if (Array.isArray(config.mqttNotificationsTopics) && config.mqttNotificationsTopics.length) {
      lines.push(`mqttNotificationsTopics: ${config.mqttNotificationsTopics.join(', ')}`);
    }
  } else {
    lines.push('mqtt: disabled');
  }

  for (const [nestedKey, allowedKeys] of Object.entries(NESTED_ALLOW)) {
    appendNestedLines(lines, nestedKey, config[nestedKey], allowedKeys);
  }

  const botSummary = summarizeBotAi(config.bot_ai);
  if (botSummary) lines.push(`bot_ai: ${botSummary}`);

  lines.push('');
  return lines.join('\n');
}

/**
 * Claves de config.json que no salen en el resto del diálogo (secretos redactados).
 */
export function buildOthersConfigBlock(config = {}) {
  const lines = ['=== OTHERS ==='];
  const keys = Object.keys(config).sort((a, b) => a.localeCompare(b));
  const mqttDetailsShown = isMqttEnabled(config);
  const mqttDetailKeys = new Set([
    'mqttBrokerUrl',
    'mqttNotificationsTopic',
    'mqttNotificationsTopics',
  ]);

  for (const key of keys) {
    if (COVERED_TOP_LEVEL.has(key)) continue;
    if (mqttDetailsShown && mqttDetailKeys.has(key)) continue;

    if (NESTED_ALLOW[key] && config[key] && typeof config[key] === 'object' && !Array.isArray(config[key])) {
      const allowed = new Set(NESTED_ALLOW[key]);
      for (const nestedKey of Object.keys(config[key]).sort((a, b) => a.localeCompare(b))) {
        if (allowed.has(nestedKey)) continue;
        if (isSecretKey(nestedKey)) {
          lines.push(`${key}.${nestedKey}: (redacted)`);
          continue;
        }
        lines.push(`${key}.${nestedKey}: ${formatOthersValue(nestedKey, config[key][nestedKey])}`);
      }
      continue;
    }

    lines.push(`${key}: ${formatOthersValue(key, config[key])}`);
  }

  if (lines.length === 1) return '';
  lines.push('');
  return lines.join('\n');
}

/**
 * Config completo para copiar: versiones + snapshot de config.
 */
export function appendSupportConfigToVersionText(versionText, config = {}) {
  let base = (versionText || '').trimEnd();
  const support = buildSupportConfigBlock(config).trimEnd();
  if (!base) {
    base = support;
  } else if (!base.includes('=== RUNTIME CONFIG (support) ===')) {
    base = `${base}\n\n${support}`;
  }

  const others = buildOthersConfigBlock(config).trimEnd();
  if (!others) return `${base}\n`;
  if (base.includes('=== OTHERS ===')) {
    return `${base.replace(/\n*=== OTHERS ===[\s\S]*$/, '')}\n\n${others}\n`;
  }
  return `${base}\n\n${others}\n`;
}
